import React from "react";
import { Zap, TrendingUp, BarChart3 } from "lucide-react";

const PILLARS = [
  {
    icon: Zap,
    accent: "#0284C7",
    title: "Radical Simplification",
    intro: "Simplify and redesign data structures to support your analytics function \u2014 eliminating data chaos, cutting manual waste, and accelerating decision-making.",
    items: [
      ["Modernization & Automated Data Pipelines", "Design data warehouse structures to support cloud-based analytical frameworks, supported through robust automated ETL workflows, and overhaul legacy processes to remove redundancies."],
      ["Agentic AI Workflow Integration", "Design and embed modern AI tools into day-to-day operations to boost efficiency and speed-to-market."],
    ],
  },
  {
    icon: TrendingUp,
    accent: "#0D9488",
    title: "Operational Excellence",
    intro: "Structured projects that fix dysfunctional data organizations, build high-performing cultures, and align stakeholders.",
    items: [
      ["Analytics Function Launch & Scale", "Design and launch a brand-new analytics department or infrastructure tailored to your business size."],
      ["Achieve Cross-Functional Alignment", "Map and restructure dysfunctional data silos, establish unified and effective performance metrics, and ensure line-of-sight transparency so every business unit executes against a single version of the truth."],
      ["Data Democratization & Self-Service", "Deploy intuitive, self-service dashboards that simplify complex portfolios and speed up go-to-market."],
    ],
  },
  {
    icon: BarChart3,
    accent: "#10B981",
    title: "Data-Driven Decision Support",
    intro: "Advanced modeling and analytics projects designed to maximize ROI, reduce acquisition costs, and drive growth.",
    items: [
      ["Performance Measurement Frameworks", "Design optimal analytics KPIs and A/B testing frameworks."],
      ["Predictive & Prescriptive Analytics", "Design and deploy AI/ML and other models that pinpoint and validate high-priority expansion opportunities and generate sales-unit and revenue forecasts, or other operational forecasts."],
    ],
  },
];

export default function Services() {
  return (
    <div className="max-w-5xl mx-auto fade-up" data-testid="services-page">
      <img src="/qls-lettermark.png" alt="Quantum Leap Solutions" data-testid="services-logo"
        className="h-9 md:h-11 w-auto mb-5 object-contain" />
      <div className="flex items-center gap-2 text-[#0284C7] mb-1">
        <BarChart3 className="w-5 h-5" />
        <span className="mono text-xs uppercase tracking-[0.25em]">What we do</span>
      </div>
      <h1 className="trust-font text-2xl md:text-3xl font-bold tracking-tight text-[#0B2830] mb-2">Our services</h1>
      <p className="text-sm md:text-base text-[#47616B] max-w-3xl mb-8">
        Our project-based consulting is built on three fundamental pillars, each delivering elite,
        tailored analytics expertise for your exact business objectives. Implemented with agile and lean
        methodologies to optimize resources and cut operational waste.
      </p>

      <div className="grid md:grid-cols-3 gap-6">
        {PILLARS.map((p, i) => (
          <div key={p.title} data-testid={`pillar-${i + 1}`}
            className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-6 flex flex-col">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
              style={{ background: `${p.accent}18` }}>
              <p.icon className="w-5 h-5" style={{ color: p.accent }} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1">Pillar {i + 1}</div>
            <h2 className="trust-font text-lg font-semibold text-[#0B2830]" style={{ color: p.accent }}>{p.title}</h2>
            <p className="text-sm text-[#47616B] mt-2 mb-4 leading-relaxed">{p.intro}</p>
            <div className="space-y-3 mt-auto">
              {p.items.map(([t, d]) => (
                <div key={t} className="border-t border-[#E6EEF1] pt-3">
                  <div className="text-sm font-semibold text-[#0B2830]">{t}</div>
                  <div className="text-xs text-[#47616B] leading-relaxed mt-0.5">{d}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
